import axios from 'axios'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const frenchPluralRules = new Intl.PluralRules('fr')

/**
 * Suffixe pluriel français (`''` ou `'s'`) selon `count`, via `Intl.PluralRules`.
 * En français, 0 et 1 sont singuliers (« 0 permission manquante »).
 * Ex. : `Permission${frenchPluralSuffix(n)} manquante${frenchPluralSuffix(n)}`.
 */
export function frenchPluralSuffix(count: number): '' | 's' {
  return frenchPluralRules.select(count) === 'one' ? '' : 's'
}

export function formatPercentage(
  percentage: number,
  options?: { withSign?: boolean; withPercent?: boolean },
) {
  const { withSign = true, withPercent = true } = options ?? {}

  let result = ''
  if (Number.isNaN(percentage) || percentage === Number.NEGATIVE_INFINITY) {
    result = '0'
  } else if (percentage === Number.POSITIVE_INFINITY) {
    result = '100'
  } else {
    result = percentage < 10 ? percentage.toFixed(1) : formatNumber(percentage)
  }

  result = `${result.replace('.', ',')}${withPercent ? '%' : ''}`

  if (withSign) {
    result = `${percentage > 0 ? '+' : ''}${result}`
  }

  return result
}

export function getAxiosErrorMessage(error: unknown, fallbackMessage?: string) {
  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  ) {
    return error.response.data.message
  }

  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    'error' in error.response.data &&
    typeof error.response.data.error === 'string'
  ) {
    return error.response.data.error
  }

  return fallbackMessage ?? 'Une erreur inconnue est survenue, veuillez contacter le support'
}

export function getTuyauErrorMessage(error: unknown, fallbackMessage?: string) {
  if (error && typeof error === 'object' && 'response' in error) {
    const body = (error as { response?: unknown }).response
    if (body && typeof body === 'object') {
      if ('error' in body && typeof body.error === 'string') return body.error
      if ('message' in body && typeof body.message === 'string') return body.message
      if (
        'errors' in body &&
        Array.isArray(body.errors) &&
        typeof body.errors[0]?.message === 'string'
      ) {
        return body.errors[0].message
      }
    }
  }

  if (error instanceof Error && error.message) return error.message

  return fallbackMessage ?? 'Une erreur inconnue est survenue, veuillez contacter le support'
}

export function trimSeconds<T extends string | null>(time: T): T {
  if (!time) return time
  return time.slice(0, 5) as T
}

export const numberFormatter = new Intl.NumberFormat('fr-FR')
export const euroFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export const formatNumber = (value: number) => numberFormatter.format(Math.round(value))

export const formatCentsToEuro = (value: number) => euroFormatter.format(Math.round(value / 100))

export const formatNumberKEuros = (value: number) =>
  `${numberFormatter.format(Math.round(value / 1000))}k €`
